/* ==========================================================================
   Homepage Reviews - BB2  →  sections/homepage-reviews-bb2.liquid
   Fade carousel for the customer quote block.

   Loaded with `defer`, so the definition may register before or after the
   section markup exists — custom elements upgrade either way, which is also
   what keeps this working when the theme editor re-renders the section.

   Auto-rotation is a mobile-only behaviour. The cut-off is the same 1024px the
  section already uses to split its mobile and desktop settings, and it is live:
  resizing or rotating past it starts or stops the timer.

  Autoplay is held (not cancelled) while any of these are true:
     - the viewport is at desktop width   → this is a mobile-only behaviour
     - the pointer is over the carousel   → transient
     - focus is inside the carousel       → transient
     - the section is scrolled off screen → transient, saves timers + repaints
     - the tab is in the background       → transient, saves battery
   ========================================================================== */

(function () {
  'use strict';

  if (customElements.get('hp-reviews-bb2')) return;

  // Queried once at module scope rather than on every timer start.
  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  var MOBILE = window.matchMedia('(max-width: 1023px)');

  function onMediaChange(mql, handler) {
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler); // older Safari
  }

  function offMediaChange(mql, handler) {
    if (mql.removeEventListener) mql.removeEventListener('change', handler);
    else mql.removeListener(handler);
  }

  customElements.define(
    'hp-reviews-bb2',
    class HpReviewsBB2 extends HTMLElement {
      connectedCallback() {
        this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-review-slide]'));
        this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-review-dot]'));
        this.stage = this.querySelector('[data-reviews-stage]');
        if (this.slides.length < 2) return;

        this.index = this.slides.findIndex(function (slide) {
          return slide.classList.contains('is-active');
        });
        if (this.index < 0) this.index = 0;

        // How many dots may show at once. Liquid still renders one per review so
        // every slide stays addressable; the extras are hidden and the window
        // slides to follow the active one — otherwise 17 reviews lay 364px of
        // dots into a 327px phone container.
        // Defaults to 10 rather than 0: the setting's floor is 4, so 0 is not a
        // reachable value, and falling back to 0 would silently switch the window
        // off if the attribute ever arrived empty.
        this.maxDots = parseInt(this.dataset.maxDots, 10) || 10;

        this.speed = parseInt(this.dataset.autoplaySpeed, 10) || 7000;
        this.wantsAutoplay = this.dataset.autoplay === 'true' && !REDUCED_MOTION.matches;
        this.timer = null;

        // Hold flags — autoplay only runs when every one of them is false.
        this.atDesktop = !MOBILE.matches;
        this.hovering = false;
        this.focusWithin = false;
        this.offScreen = false;
        this.tabHidden = document.visibilityState === 'hidden';

        this.bindControls();
        this.watchVisibility();
        this.watchBreakpoint();
        this.windowDots();
        this.sync();
      }

      disconnectedCallback() {
        this.stop();
        if (this.observer) this.observer.disconnect();
        if (this.onVisibilityChange) {
          document.removeEventListener('visibilitychange', this.onVisibilityChange);
        }
        if (this.onBreakpoint) offMediaChange(MOBILE, this.onBreakpoint);
      }

      watchBreakpoint() {
        var self = this;
        this.onBreakpoint = function () {
          self.atDesktop = !MOBILE.matches;
          self.sync();
        };
        onMediaChange(MOBILE, this.onBreakpoint);
      }

      bindControls() {
        var self = this;

        var prev = this.querySelector('[data-review-prev]');
        var next = this.querySelector('[data-review-next]');
        if (prev) prev.addEventListener('click', function () { self.step(-1); });
        if (next) next.addEventListener('click', function () { self.step(1); });

        this.dots.forEach(function (dot, i) {
          dot.addEventListener('click', function () { self.go(i); });
        });

        // Left / right arrows move between reviews while focus is in the controls.
        var nav = this.querySelector('[data-review-nav]');
        if (nav) {
          nav.addEventListener('keydown', function (event) {
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              self.step(-1);
            } else if (event.key === 'ArrowRight') {
              event.preventDefault();
              self.step(1);
            }
          });
        }

        this.addEventListener('mouseenter', function () { self.hovering = true; self.sync(); });
        this.addEventListener('mouseleave', function () { self.hovering = false; self.sync(); });
        this.addEventListener('focusin', function () { self.focusWithin = true; self.sync(); });
        this.addEventListener('focusout', function () { self.focusWithin = false; self.sync(); });
      }

      watchVisibility() {
        var self = this;

        this.onVisibilityChange = function () {
          self.tabHidden = document.visibilityState === 'hidden';
          self.sync();
        };
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        if (!('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver(
          function (entries) {
            self.offScreen = !entries[0].isIntersecting;
            self.sync();
          },
          { threshold: 0 }
        );
        this.observer.observe(this);
      }

      show(i) {
        this.index = (i + this.slides.length) % this.slides.length;

        this.slides.forEach(function (slide, k) {
          slide.classList.toggle('is-active', k === this.index);
        }, this);

        this.dots.forEach(function (dot, k) {
          dot.setAttribute('aria-current', k === this.index ? 'true' : 'false');
        }, this);

        this.windowDots();
      }

      // Keep at most maxDots visible, in fixed pages: dots 1-10, then 11-17.
      // The active dot always moves one slot on every press, and the row only
      // changes once per page.
      //
      // Two approaches were tried and rejected, both for the same reason — they
      // hold the active dot still and slide the row underneath it, so pressing
      // next reads as a dead button. Re-centring on the active dot does it on
      // every press; a window that creeps by one does it for the whole tail.
      // Paging is the only one where the press visibly does something every time.
      //
      // The trade-off is that a final short page is narrower than a full one. The
      // row is centred, so it simply gets shorter — a far smaller artefact than a
      // button that looks broken.
      windowDots() {
        var total = this.dots.length;
        var max = this.maxDots;
        if (!max || total <= max) return;

        var start = Math.floor(this.index / max) * max;

        this.dots.forEach(function (dot, k) {
          dot.hidden = k < start || k >= start + max;
        });
      }

      // Any manual move restarts the dwell so the chosen review gets a full turn.
      step(delta) {
        this.show(this.index + delta);
        this.sync();
      }

      go(i) {
        this.show(i);
        this.sync();
      }

      // Rotating content should not also be announced — that would talk over the
      // visitor. Wherever it is not rotating (desktop, or autoplay off) the stage
      // becomes a polite live region so arrow / dot moves are read out.
      syncLiveRegion(running) {
        if (!this.stage) return;
        this.stage.setAttribute('aria-live', running ? 'off' : 'polite');
      }

      shouldRun() {
        return (
          this.wantsAutoplay &&
          !this.atDesktop &&
          !this.hovering &&
          !this.focusWithin &&
          !this.offScreen &&
          !this.tabHidden
        );
      }

      sync() {
        var running = this.shouldRun();
        this.syncLiveRegion(this.wantsAutoplay && !this.atDesktop);
        if (running) {
          this.start();
        } else {
          this.stop();
        }
      }

      start() {
        var self = this;
        this.stop();
        this.timer = setInterval(function () { self.show(self.index + 1); }, this.speed);
      }

      stop() {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
      }
    }
  );
})();
